import { Box, Flex, Icon, Spinner, Stack, StackProps } from '@chakra-ui/react';
import React, { FunctionComponent as FC } from 'react';
import { useSelectData } from './MCustomSelect/components/useSelectData';
import MRadio from './MRadio';
import MRadioGroup, { MRadioGroupProps } from './MRadioGroup';
import MText from './MText';

interface MCustomRadioGroupProps extends Omit<
  MRadioGroupProps,
  'children' | 'value' | 'onChange'
> {
  children?: any;
  items?: any[];
  itemTitle?: string;
  itemValue?: string;
  itemDescription?: string | React.ReactElement;
  endpoint?: string;
  loadAll?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  stackProps?: StackProps;
}

const MCustomRadioGroup: FC<MCustomRadioGroupProps> = React.forwardRef<
  HTMLDivElement,
  MCustomRadioGroupProps
>(
  (
    {
      children,
      items: externalItems,
      itemTitle = 'title',
      itemValue = 'value',
      itemDescription,
      endpoint,
      loadAll,
      disabled,
      value,
      onChange,
      placeholder,
      stackProps,
      ...rest
    }: MCustomRadioGroupProps,
    ref,
  ) => {
    const { getValue, items, getTitle, loading } = useSelectData({
      endpoint,
      loadAll,
      itemValue,
      externalItems,
      itemTitle,
    });

    const getDescription = (item: any) =>
      item ? item[itemDescription as any] : '';

    const renderItems = () =>
      items.map((item: any, index: number) => {
        const val = getValue(item);
        const isItemDisabled = item.disabled || disabled;
        return (
          <Box key={index} opacity={isItemDisabled ? 0.8 : undefined}>
            <MRadio value={val} isDisabled={isItemDisabled}>
              <MText
                color={isItemDisabled ? 'tGray.darkPurple' : 'tPurple.base'}
                truncate
                lineClamp={1}
              >
                {getTitle(item)}
              </MText>
            </MRadio>
            {!!itemDescription && (
              <MText color="tGray.darkPurple" ml="5">
                {getDescription(item)}
              </MText>
            )}
          </Box>
        );
      });

    if (loading) {
      return <Spinner size="xs" />;
    }

    return (
      <MRadioGroup
        ref={ref}
        position="relative"
        w="100%"
        value={value ?? undefined}
        onChange={onChange}
        {...rest}
      >
        <Stack gap={4} direction="column" {...stackProps}>
          {renderItems()}
        </Stack>
      </MRadioGroup>
    );
  },
);

export const MIconAction = ({ icon }: { icon: any }) => (
  <Icon
    fontSize="lg"
    as={icon}
    minWidth="0"
    background="none"
    _hover={{
      color: 'tIndigo.base',
      background: 'none',
    }}
  />
);

export const MCustomRadioGroupItem = ({ children, ...rest }: any) => (
  <Flex
    color="tIndigo.base"
    p="2"
    borderRadius="2"
    cursor="pointer"
    _hover={{ color: 'white', background: 'tIndigo.base' }}
    alignItems="center"
    {...rest}
  >
    {children}
  </Flex>
);

export default MCustomRadioGroup;
